/**
 * MINI-JEU — MEMORY DES CRÉATURES. Jeu de paires (6 paires / 12 cartes) dont les faces
 * sont les créatures de l'enfant (possédées en priorité, complétées sinon). Gagner
 * appelle /api/child/minigame/reward → 70% de chance de débloquer une créature.
 * Thémé auto via les variables --kid-* (cyan Isaac / rose Bao). Premier jeu d'un futur
 * "arcade" JARVIS (cadre + rotation "jeu du jour" à venir).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createGameAudio, type GameAudio } from "./gameSounds";

type Face = { key: string; emoji: string; name: string };
type Card = { id: number; face: Face; matched: boolean };
type Drop = { emoji: string; name: string; rarity: string } | null;

const PAIRS = 6;
const FALLBACK: Face[] = [
  { key: "f1", emoji: "🦖", name: "Dino" }, { key: "f2", emoji: "🦄", name: "Licorne" },
  { key: "f3", emoji: "🚀", name: "Fusée" }, { key: "f4", emoji: "🐉", name: "Dragon" },
  { key: "f5", emoji: "🤖", name: "Robot" }, { key: "f6", emoji: "🦋", name: "Papillon" },
  { key: "f7", emoji: "🌟", name: "Étoile" }, { key: "f8", emoji: "🐢", name: "Tortue" },
];

const shuffle = <T,>(a: T[]): T[] => {
  const b = a.slice();
  for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [b[i], b[j]] = [b[j], b[i]]; }
  return b;
};

function buildDeck(faces: Face[]): Card[] {
  const chosen = faces.slice(0, PAIRS);
  const cards: Card[] = [];
  chosen.forEach((f, k) => {
    cards.push({ id: k * 2, face: f, matched: false });
    cards.push({ id: k * 2 + 1, face: f, matched: false });
  });
  return shuffle(cards);
}

export default function MemoryGame({ apiBase, onClose }: { apiBase: string; onClose: () => void }) {
  const [deck, setDeck] = useState<Card[]>([]);
  const [flipped, setFlipped] = useState<number[]>([]); // indices in deck currently face-up (not matched)
  const [busy, setBusy] = useState(false);
  const [moves, setMoves] = useState(0);
  const [won, setWon] = useState(false);
  const [reward, setReward] = useState<Drop>(null);
  const sfx = useRef<GameAudio | null>(null);
  if (!sfx.current) sfx.current = createGameAudio();
  useEffect(() => () => sfx.current?.dispose(), []);

  const pickFaces = useCallback(async (): Promise<Face[]> => {
    try {
      const r = await fetch(`${apiBase}/api/child/collection`);
      if (r.ok) {
        const d = await r.json();
        const all: { emoji: string; name: string; owned: boolean }[] = [];
        for (const room of Object.values<any>(d.rooms)) for (const c of room.creatures) all.push(c);
        const seen = new Set<string>();
        const dedup = all.filter((c) => (seen.has(c.emoji) ? false : (seen.add(c.emoji), true)));
        dedup.sort((a, b) => Number(b.owned) - Number(a.owned)); // possédées d'abord
        const faces = dedup.map((c, i) => ({ key: "c" + i, emoji: c.emoji, name: c.name }));
        if (faces.length >= PAIRS) return faces;
        return [...faces, ...FALLBACK].slice(0, PAIRS + 2);
      }
    } catch {
      /* offline → fallback */
    }
    return FALLBACK;
  }, [apiBase]);

  const start = useCallback(async () => {
    setWon(false); setReward(null); setFlipped([]); setMoves(0); setBusy(false);
    const faces = await pickFaces();
    setDeck(buildDeck(shuffle(faces)));
  }, [pickFaces]);

  useEffect(() => { start(); }, [start]);

  const onWin = useCallback(async () => {
    setWon(true);
    sfx.current?.win();
    try {
      const r = await fetch(`${apiBase}/api/child/minigame/reward`, { method: "POST" });
      const d = await r.json();
      if (d && d.drop) setReward(d.drop);
    } catch {
      /* pas de récompense si offline */
    }
  }, [apiBase]);

  const tap = (i: number) => {
    if (busy || won) return;
    const card = deck[i];
    if (!card || card.matched || flipped.includes(i)) return;
    const nf = [...flipped, i];
    setFlipped(nf);
    sfx.current?.flip();
    if (nf.length === 2) {
      setBusy(true);
      setMoves((m) => m + 1);
      const [a, b] = nf;
      if (deck[a].face.key === deck[b].face.key) {
        sfx.current?.match();
        setTimeout(() => {
          setDeck((cur) => {
            const nd = cur.map((c, idx) => (idx === a || idx === b ? { ...c, matched: true } : c));
            if (nd.every((c) => c.matched)) onWin();
            return nd;
          });
          setFlipped([]);
          setBusy(false);
        }, 380);
      } else {
        setTimeout(() => { setFlipped([]); setBusy(false); }, 820);
      }
    }
  };

  const rar = reward?.rarity;
  const rewardCol = rar === "legendary" ? "#ffd43b" : rar === "rare" ? "var(--kid-cyan)" : "var(--kid-text-3)";

  return (
    <div style={S.screen}>
      <style>{CSS}</style>
      <div style={S.header}>
        <button style={S.close} onClick={onClose} aria-label="Fermer">✕</button>
        <div style={S.title}>MEMORY DES CRÉATURES</div>
        <div style={S.moves}>{moves} coups</div>
      </div>

      <div style={S.boardWrap}>
        <div style={S.board}>
          {deck.map((c, i) => {
            const up = c.matched || flipped.includes(i);
            return (
              <button
                key={c.id}
                className={`mem-card${up ? " up" : ""}${c.matched ? " matched" : ""}`}
                onClick={() => tap(i)}
                aria-label={up ? c.face.name : "carte"}
              >
                <span className="mem-face">{up ? c.face.emoji : "🎴"}</span>
              </button>
            );
          })}
        </div>
      </div>

      {won && (
        <div style={S.winOverlay} onClick={start}>
          <div style={S.winCard}>
            <div style={{ fontSize: 40 }}>🏆</div>
            <div style={{ color: "var(--kid-cyan)", fontWeight: 800, fontSize: 20 }}>GAGNÉ !</div>
            <div style={S.winSub}>en {moves} coups</div>
            {reward ? (
              <div style={{ ...S.rewardBox, borderColor: rewardCol }}>
                <div style={{ fontSize: 44 }}>{reward.emoji}</div>
                <div style={{ fontWeight: 700 }}>{reward.name}</div>
                <div style={{ color: rewardCol, fontSize: 12, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.14em" }}>
                  CRÉATURE DÉBLOQUÉE&nbsp;!
                </div>
              </div>
            ) : (
              <div style={S.winSub}>Pas de créature cette fois — rejoue pour retenter&nbsp;!</div>
            )}
            <button style={S.replay} onClick={(e) => { e.stopPropagation(); start(); }}>🔄 Rejouer</button>
            <button style={S.leave} onClick={(e) => { e.stopPropagation(); onClose(); }}>Quitter</button>
          </div>
        </div>
      )}
    </div>
  );
}

const CSS = `
.mem-card { aspect-ratio: 3/4; border-radius: 12px; border: 1.5px solid var(--kid-cyan-40); background: rgba(0,212,255,0.06); display:flex; align-items:center; justify-content:center; cursor:pointer; padding:0; transition: transform .12s ease, background .2s ease, border-color .2s ease; }
.mem-card .mem-face { font-size: clamp(24px, 9vw, 40px); line-height:1; }
.mem-card:not(.up) .mem-face { opacity: 0.55; filter: grayscale(1); }
.mem-card.up { background: rgba(255,255,255,0.06); }
.mem-card.matched { border-color: var(--kid-green); background: var(--kid-green-10); }
.mem-card:active { transform: scale(0.95); }
@keyframes mem-pop { from { transform: scale(.6); opacity:0 } to { transform: scale(1); opacity:1 } }
`;

const S: Record<string, React.CSSProperties> = {
  screen: { position: "fixed", inset: 0, zIndex: 62, background: "var(--kid-bg)", display: "flex", flexDirection: "column", color: "var(--kid-text)", fontFamily: "var(--kid-font-sans, system-ui)" },
  header: { display: "flex", alignItems: "center", gap: 12, padding: "16px 16px 8px", borderBottom: "1px solid var(--kid-border)" },
  close: { width: 40, height: 40, borderRadius: 12, border: "1px solid var(--kid-cyan-40)", background: "transparent", color: "var(--kid-cyan)", fontSize: 18, cursor: "pointer" },
  title: { flex: 1, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.14em", fontSize: 15, color: "var(--kid-cyan)" },
  moves: { fontFamily: "var(--kid-font-mono)", fontSize: 13, color: "var(--kid-text-3)" },
  boardWrap: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  board: { width: "100%", maxWidth: 420, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 },
  winOverlay: { position: "fixed", inset: 0, zIndex: 64, background: "rgba(3,5,9,0.8)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  winCard: { textAlign: "center", padding: "26px 30px", borderRadius: 20, border: "1px solid var(--kid-cyan-40)", background: "var(--kid-bg)", animation: "mem-pop .35s ease", maxWidth: 340, width: "100%" },
  winSub: { color: "var(--kid-text-3)", fontSize: 13, marginTop: 4 },
  rewardBox: { margin: "16px auto 4px", padding: "14px", borderRadius: 16, border: "2px solid var(--kid-cyan)", display: "flex", flexDirection: "column", gap: 4, alignItems: "center" },
  replay: { display: "block", width: "100%", marginTop: 18, padding: "13px", borderRadius: 14, border: "none", background: "linear-gradient(180deg, var(--kid-cyan), #008cbe)", color: "#04222c", fontSize: 16, fontWeight: 800, cursor: "pointer" },
  leave: { display: "block", width: "100%", marginTop: 10, padding: "11px", borderRadius: 12, border: "1px solid var(--kid-cyan-40)", background: "transparent", color: "var(--kid-text-2)", fontSize: 14, cursor: "pointer" },
};
